import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView } from 'src/Libs/chart-kit'
import { Breakthrough, ForecastPower } from 'src/Libs/system-data-handler'
import {
  CoreHistoryChartData,
  HistoryChartDataPoc,
  PowermeterHistoryChartData,
  WallboxHistoryChartData,
} from 'src/Libs/system-data-handler/models/history-data'
import { binarySearchObject } from 'src/Libs/system-data-handler/utils/binarySearch'
import { Resolution } from 'src/Libs/chart-kit/shared/types'
import { incrementDateByResolution } from 'src/Libs/chart-kit/shared/utils/csv-export-fns'

/**
 * For the POC performant chart 3 types of data are fetched: core history data, wallbox data and powermeter data.
 * All are available as arrays of objects with a timestamp and various values. This function merges the data into one array based on the timestamp.
 */
export const mergeDataArraysBasedOnTimestamp = (
  historyCoreData: CoreHistoryChartData[],
  wallboxData: WallboxHistoryChartData[],
  powermeterData: PowermeterHistoryChartData[],
  forecastPowerData: ForecastPower[],
  breakthroughData: Breakthrough[],
  from: Moment,
  to: Moment,
  view: ChartView,
  resolution?: Resolution
): HistoryChartDataPoc[] => {
  let timestamp = from.clone()
  const historyDataPoc = []

  while (timestamp.isBefore(to)) {
    const emptyEntry = {
      time: timestamp.valueOf(),
      ts: timestamp.valueOf(),
    }

    const matchingHistoryItem = binarySearchObject(historyCoreData, timestamp.valueOf(), 'from') ?? emptyEntry
    const matchingWallboxItem = binarySearchObject(wallboxData, timestamp.valueOf(), 'from')
    const matchingPowerMeterItem = binarySearchObject(powermeterData, timestamp.valueOf(), 'from') ?? emptyEntry
    const matchingForecastPower = binarySearchObject(forecastPowerData, timestamp.valueOf(), 'time')?.power ?? 0
    const isBreakthrough = Boolean(binarySearchObject(breakthroughData, timestamp.valueOf(), 'deratePowerAt'))

    historyDataPoc.push({
      ...matchingHistoryItem,
      ...matchingWallboxItem,
      ...matchingPowerMeterItem,
      ForecastPower: matchingForecastPower,
      WeatherBreakthrough: isBreakthrough,
      time: timestamp.valueOf(),
      ts: timestamp.valueOf(),
    })

    timestamp = view !== 'day' && resolution ? moment(incrementDateByResolution(timestamp.toDate(), resolution)) : increaseTimestamp(timestamp, view)
  }

  return historyDataPoc as HistoryChartDataPoc[]
}

export const increaseTimestamp = (currentTimestamp: Moment, view: ChartView): Moment => {
  const copyTimestamp = currentTimestamp.clone()
  if (view === 'day') return copyTimestamp.add(15, 'minutes')
  if (view === 'week') return copyTimestamp.add(1, 'hour')
  if (view === 'month') return copyTimestamp.add(1, 'day')
  if (view === 'year') return copyTimestamp.add(1, 'month')

  return copyTimestamp
}
